import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Plus, Trash2, Search, Landmark, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

const TIPOS = [
  { value: "tarjeta", label: "Tarjeta", icon: CreditCard },
  { value: "transferencia", label: "Transferencia", icon: ArrowLeftRight },
  { value: "domiciliacion", label: "Domiciliación", icon: Landmark },
];
const tipoMeta = (t) => TIPOS.find((x) => x.value === t) || TIPOS[1];
const emptyForm = { nombre: "", tipo: "tarjeta", activo: true };

export default function MetodosPagoPage() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");

  const { data: metodos = [], isLoading } = useQuery({
    queryKey: ["metodos-pago"],
    queryFn: async () => (await api.get("/metodos-pago")).data,
  });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/metodos-pago", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["metodos-pago"] });
      toast.success("Método de pago creado");
      setDialogOpen(false);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, activo }) => api.patch(`/metodos-pago/${id}`, { activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metodos-pago"] }),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/metodos-pago/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["metodos-pago"] });
      toast.success("Método eliminado");
      setToDelete(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return metodos.filter((m) =>
      (tipoFilter === "all" || m.tipo === tipoFilter) &&
      (!q || m.nombre.toLowerCase().includes(q) || m.label.toLowerCase().includes(q)),
    );
  }, [metodos, search, tipoFilter]);

  const counts = useMemo(() => {
    const c = { total: metodos.length, tarjeta: 0, transferencia: 0, domiciliacion: 0 };
    metodos.forEach((m) => { c[m.tipo] = (c[m.tipo] || 0) + 1; });
    return c;
  }, [metodos]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    createMut.mutate({ nombre: form.nombre.trim(), tipo: form.tipo, activo: form.activo });
  };

  return (
    <div className="space-y-6" data-testid="metodos-pago-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <CreditCard className="h-6 w-6 text-primary" /> Métodos de pago
          </h1>
          <p className="text-sm text-muted-foreground">
            Maestro de medios de pago a proveedores. Se usan en la pestaña «Pagos» de cada reserva.
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} data-testid="mp-add-btn">
          <Plus className="mr-1 h-4 w-4" /> Nuevo método
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar método…"
            className="w-64 pl-8"
            data-testid="mp-search"
          />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-48" data-testid="mp-tipo-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos ({counts.total})</SelectItem>
            {TIPOS.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label} ({counts[t.value] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-40">Tipo</TableHead>
                  <TableHead className="w-28 text-center">Activo</TableHead>
                  <TableHead className="w-20 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="mp-table-body">
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">Sin métodos. Pulsa «Nuevo método».</TableCell></TableRow>
                ) : filtered.map((m) => {
                  const meta = tipoMeta(m.tipo);
                  const Icon = meta.icon;
                  return (
                    <TableRow key={m.id} data-testid={`mp-row-${m.id}`} className={m.activo ? "" : "opacity-50"}>
                      <TableCell className="font-medium">{m.nombre}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="gap-1 font-normal">
                          <Icon className="h-3 w-3" /> {meta.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={!!m.activo}
                          onCheckedChange={(v) => toggleMut.mutate({ id: m.id, activo: v })}
                          data-testid={`mp-toggle-${m.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setToDelete(m)} data-testid={`mp-del-${m.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="mp-dialog">
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>Nuevo método de pago</DialogTitle>
              <DialogDescription>Indica el nombre (p. ej. «AMEX MARINA», «CAIXA EUROS AGENTES 2616») y el tipo.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="AMEX MARINA"
                  autoFocus
                  data-testid="mp-form-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="mp-form-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`mp-form-tipo-${t.value}`}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <Label className="text-sm">Activo</Label>
                <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} data-testid="mp-form-activo" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={createMut.isPending} data-testid="mp-form-submit">
                {createMut.isPending ? "Creando…" : "Crear método"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="mp-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{toDelete?.label}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Dejará de aparecer en el desplegable de nuevos pagos. Los pagos ya registrados conservan su etiqueta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMut.mutate(toDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="mp-delete-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
